/**
 * Turn "this tab is running a stale bundle" into one of two outcomes.
 *
 *   mount already stale            ──► navigation (the route just changed)
 *   routeKey changed since detect  ──► navigation
 *   became stale on this route     ──► poll
 *
 * The caller mounts this below the route-scoped editing store, so a mount that
 * is ALREADY stale can only mean the user navigated after detection — which is
 * the safe moment to swap bundles.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import {
  decideStaleBundleAction,
  type StaleBundleAction,
  type StaleBundleMonitor,
  type StaleBundleStatus,
  type StaleBundleTrigger,
} from 'src/lib/buildVersion';

export type UseStaleBundleReloadOptions = {
  monitor: StaleBundleMonitor;
  /** False in dev/test builds, where there is no deployed bundle to go stale. */
  enabled: boolean;
  /** True while a grid row, debounced draft, in-flight save or chat upload is outstanding. */
  hasUnsavedWork: boolean;
  /** Recheck scoped work registered after render, before taking an automatic swap. */
  getHasUnsavedWork?: () => boolean;
  /** Changes on navigation — the current pathname. */
  routeKey: string;
  /**
   * Called at most once per mount when the swap must be offered, not taken.
   * Handed the SAME `reload` the automatic path uses, so an offer the user
   * accepts cannot take a less careful route than the one just declined.
   */
  onPrompt: (status: Extract<StaleBundleStatus, { kind: 'stale' }>, reload: () => void) => void;
  /**
   * Required, with no default. A bare `window.location.reload` would discard
   * whatever the user had outstanding, which is the very thing the prompt
   * exists to avoid — so every caller states which reload it means.
   */
  reload: () => void;
};

export function useStaleBundleReload({
  monitor,
  enabled,
  hasUnsavedWork,
  getHasUnsavedWork,
  routeKey,
  onPrompt,
  reload,
}: UseStaleBundleReloadOptions): StaleBundleAction {
  const subscribe = useCallback((listener: () => void) => monitor.subscribe(listener), [monitor]);
  const status = useSyncExternalStore(subscribe, monitor.getStatus, monitor.getStatus);

  useEffect(() => {
    if (!enabled) return;
    monitor.start();
  }, [enabled, monitor]);

  const mountedStaleRef = useRef(status.kind === 'stale');
  const mountRouteKeyRef = useRef(routeKey);
  const promptedRef = useRef(false);
  const promptedRouteKeyRef = useRef<string | null>(null);
  const reloadedRef = useRef(false);

  const isStale = status.kind === 'stale';
  const trigger: StaleBundleTrigger =
    !mountedStaleRef.current && routeKey === mountRouteKeyRef.current ? 'poll' : 'navigation';
  const action = decideStaleBundleAction({ hasUnsavedWork, isStale, trigger });

  useEffect(() => {
    const currentAction = getHasUnsavedWork
      ? decideStaleBundleAction({ hasUnsavedWork: getHasUnsavedWork(), isStale, trigger })
      : action;
    // Taking the swap marks the tab busy while edits flush, which reads back
    // as outstanding work — so a reload already taken ends this effect.
    if (currentAction === 'ignore' || !isStale || reloadedRef.current) return;

    // An offer standing on THIS route is the user's to take: work settling by
    // itself must not reload under their hands. A navigation is a fresh, safe
    // moment, so a deferred reload is still taken there.
    if (currentAction === 'reload' && promptedRouteKeyRef.current !== routeKey) {
      reloadedRef.current = true;
      reload();
      return;
    }

    if (currentAction !== 'prompt' || promptedRef.current) return;
    promptedRef.current = true;
    promptedRouteKeyRef.current = routeKey;
    onPrompt(status as Extract<StaleBundleStatus, { kind: 'stale' }>, reload);
  }, [action, getHasUnsavedWork, isStale, onPrompt, reload, routeKey, status, trigger]);

  return action;
}
